// ELS ARQUETIPS DE SISTEMES.
//
// Fora de React i sense cap import. Es proven sols: `npm run prova:arquetips`.
//
// ── QUÈ SÓN, I PER QUÈ TENEN LLOC PROPI ─────────────────────────────────────────────────────
//
// Un arquetip **no és una plantilla**. És **una forma que es reconeix**: quan la saps, mires una
// situació i dius «això és desplaçar la càrrega». Per això cadascun porta tres coses que un
// dibuix sol no pot dir:
//
//   · **la història** — què hi passa i per què sembla que funcioni
//   · **el parany** — el que la gent sol fer i que ho empitjora, no per despistada sinó perquè
//     a curt termini funciona
//   · **el punt de palanca** — on s'ha de tocar
//
// Un desplegable d'una barra d'eines no pot portar això, i el llenç causal ja va just d'espai.
// Per això són una biblioteca amb lloc propi, i des d'allà **s'obren com un model de bucles
// causals** que ja es pot fer córrer i adaptar.
//
// ── LA FORMA DE LES DADES ───────────────────────────────────────────────────────────────────
//
// Exactament la que el llenç causal fa servir —`variables` i `relationships`— perquè obrir-ne un
// sigui desar-lo i prou. Els noms i els textos són **claus de traducció**, no text: un arquetip
// s'ha de poder llegir en els dos idiomes i el nom de cada variable surt al dibuix.
//
// ── LES POLARITATS ESTAN COMPROVADES ────────────────────────────────────────────────────────
//
// Cada arquetip declara **quants bucles ha de tenir i de quina mena**, i la prova ho comprova amb
// `buclesCausals.js` —el mateix codi que els detecta al llenç—. Un arquetip amb la polaritat
// canviada ensenyaria el contrari del que diu el seu text, i **a ull no es veu**: cal comptar els
// signes negatius de cada cicle.

// `inicial` per defecte és 50 (el centre, cap tendència) perquè la majoria de variables
// comencen sense cap senyal. Les que representen «encara no ha passat» —una condició limitadora,
// una conseqüència no volguda, un fre— han de començar BAIXES: amb totes a 50, la força que empeny
// i la que frena arrenquen igual de fortes i es cancel·len des del primer instant, i la variable
// que hauria de créixer i després baixar es queda clavada al mig tota la simulació. Es va veure
// fent córrer `motorBucles.js` de debò, no mirant el dibuix (31/10/2026).
const v = (id, clau, x, y, inicial = 50) => ({ id, clau, x, y, inicial });
const r = (from, to, polarity, delay = 0, strength = 'medium') => ({ from, to, polarity, delay, strength });

export const ARQUETIPS = [
  {
    id: 'limits',
    variables: [
      v('a', 'arq.limitsAccio', 120, 90),
      v('b', 'arq.limitsRendiment', 380, 90),
      // La condició i el fre comencen baixos: el límit encara no ha aparegut. Si comencessin a
      // 50 com tothom, es posarien al dia amb l'acció de creixement gairebé a l'instant i el
      // Rendiment no arribaria mai a créixer —es quedava clavat a 50 tota l'estona, comprovat
      // fent córrer el motor—, que és exactament el contrari del que diu la història.
      v('c', 'arq.limitsCondicio', 380, 280, 15),
      v('d', 'arq.limitsFre', 120, 280, 15),
    ],
    relacions: [
      r('a', 'b', '+'), r('b', 'a', '+'),
      r('b', 'c', '+'), r('c', 'd', '+', 2), r('d', 'b', '-'),
    ],
    esperats: { reinforcing: 1, balancing: 1 },
  },
  {
    id: 'carrega',
    variables: [
      v('s', 'arq.carregaSimptoma', 260, 60),
      v('r', 'arq.carregaRapida', 60, 200),
      v('f', 'arq.carregaFons', 460, 200),
      v('e', 'arq.carregaEfecte', 260, 330),
    ],
    relacions: [
      r('s', 'r', '+'), r('r', 's', '-'),
      r('s', 'f', '+'), r('f', 's', '-', 3),
      r('r', 'e', '+', 2), r('e', 'f', '-'),
    ],
    esperats: { reinforcing: 1, balancing: 2 },
  },
  {
    id: 'apedacen',
    variables: [
      v('p', 'arq.apedacenProblema', 120, 100),
      v('s', 'arq.apedacenSolucio', 400, 100),
      // La conseqüència comença a 0: encara no ha arribat. Començant a 50 com el Problema,
      // les dues forces sobre «p» —la solució que el baixa, la conseqüència que el puja— es
      // compensaven des del primer instant i el Problema no es movia mai de 50: ni marxava ni
      // tornava pitjor, que és tota la lliçó de l'arquetip.
      v('c', 'arq.apedacenConsequencia', 400, 290, 0),
    ],
    relacions: [
      r('p', 's', '+'), r('s', 'p', '-'),
      r('s', 'c', '+', 3), r('c', 'p', '+', 0, 'strong'),
    ],
    esperats: { reinforcing: 1, balancing: 1 },
  },
  {
    id: 'escalada',
    variables: [
      v('aa', 'arq.escaladaAmenaçaA', 100, 90),
      v('ba', 'arq.escaladaAccioA', 380, 90),
      v('ab', 'arq.escaladaAmenaçaB', 380, 290),
      v('bb', 'arq.escaladaAccioB', 100, 290),
    ],
    relacions: [
      r('aa', 'ba', '+'), r('ba', 'ab', '+', 1),
      r('ab', 'bb', '+'), r('bb', 'aa', '+', 1),
    ],
    esperats: { reinforcing: 1, balancing: 0 },
  },
  {
    id: 'exit',
    // «ra» comença amb una petita avantatge (53 i no 50) perquè hi hagi alguna cosa a
    // amplificar. Des del 31/10/2026 el motor sí que pot fer que una diferència petita es
    // faci estructural (vegeu `motorBucles.js`, `teMutua`), però **una simetria exacta es
    // queda exacta per sempre** —és correcte: un sistema sense cap diferència no té cap
    // raó per decantar-se— i amb tots a 50 l'arquetip es quedaria sense dir res, com abans.
    variables: [
      v('ra', 'arq.exitRecursosA', 110, 90, 53),
      v('ea', 'arq.exitExitA', 380, 90),
      v('rb', 'arq.exitRecursosB', 110, 300),
      v('eb', 'arq.exitExitB', 380, 300),
    ],
    relacions: [
      r('ra', 'ea', '+'), r('ea', 'ra', '+'),
      r('rb', 'eb', '+'), r('eb', 'rb', '+'),
      r('ra', 'rb', '-'), r('rb', 'ra', '-'),
    ],
    esperats: { reinforcing: 3, balancing: 0 },
  },
  {
    id: 'comuns',
    variables: [
      v('aa', 'arq.comunsActivitatA', 90, 80),
      v('ab', 'arq.comunsActivitatB', 90, 320),
      v('t', 'arq.comunsTotal', 330, 200),
      v('e', 'arq.comunsEsgotament', 540, 200),
      v('g', 'arq.comunsGuany', 330, 20),
    ],
    relacions: [
      r('aa', 't', '+'), r('ab', 't', '+'),
      r('t', 'e', '+', 3), r('e', 'g', '-'),
      r('g', 'aa', '+'), r('g', 'ab', '+'),
    ],
    esperats: { reinforcing: 0, balancing: 2 },
  },
  {
    id: 'erosio',
    variables: [
      v('o', 'arq.erosioObjectiu', 100, 90),
      v('d', 'arq.erosioDistancia', 350, 190),
      v('p', 'arq.erosioPressio', 100, 300),
      v('a', 'arq.erosioAccions', 600, 190),
      v('s', 'arq.erosioSituacio', 600, 40),
    ],
    relacions: [
      r('o', 'd', '+'), r('d', 'p', '+'), r('p', 'o', '-'),
      r('d', 'a', '+'), r('a', 's', '+', 3), r('s', 'd', '-'),
    ],
    esperats: { reinforcing: 0, balancing: 2 },
  },
  {
    id: 'infra',
    variables: [
      v('c', 'arq.infraCreixement', 100, 80),
      v('d', 'arq.infraDemanda', 360, 80),
      v('r', 'arq.infraRendiment', 360, 260),
      v('n', 'arq.infraNecessitat', 620, 260),
      v('i', 'arq.infraInversio', 620, 80),
    ],
    relacions: [
      r('c', 'd', '+'), r('d', 'c', '+'),
      r('d', 'r', '-'), r('r', 'c', '+'),
      r('r', 'n', '-'), r('n', 'i', '+', 4), r('i', 'r', '+'),
    ],
    esperats: { reinforcing: 1, balancing: 2 },
  },
  {
    id: 'retard',
    // Amb només e,d,a en cercle, CADA variable té una única entrada, i amb una única entrada
    // la seva derivada no pot canviar de signe mai: es provà amb el motor de debò, variant
    // retard i força de totes les maneres, i l'Estat sempre pujava (o baixava) en línia recta,
    // sense sobrepassar res. Calia una pressió que no esperi —aquí «pr»— perquè l'Estat tingui
    // DUES entrades de signe contrari: una que no para (la pressió) i una que arriba tard (la
    // correcció). Això és el que fa possible el sobrepassament; el bucle e→d→a→e es queda amb
    // una sola negativa i continua sent equilibrador, igual que abans (31/10/2026).
    variables: [
      v('pr', 'arq.retardPressio', 20, 10, 70),
      v('e', 'arq.retardEstat', 110, 100, 30),
      v('d', 'arq.retardDiscrepancia', 380, 100, 30),
      v('a', 'arq.retardAccio', 250, 280, 10),
    ],
    relacions: [
      r('pr', 'e', '+'), r('e', 'd', '+'), r('d', 'a', '+'), r('a', 'e', '-', 6),
    ],
    esperats: { reinforcing: 0, balancing: 1 },
  },
];

export const arquetipPerId = (id) => ARQUETIPS.find((a) => a.id === id) || null;

// Cap al format del llenç causal. Els identificadors es refan amb un prefix perquè no puguin
// xocar amb res que ja hi hagi al model on s'obri.
export function capAlLlenc(arquetip, nomDe, ara = 0) {
  if (!arquetip) return null;
  const idNou = (x) => `${arquetip.id}_${ara}_${x}`;
  return {
    variables: arquetip.variables.map((x, i) => ({
      id: idNou(x.id),
      name: nomDe(x.clau),
      type: 'variable',
      x: x.x, y: x.y,
      polarity: '+',
      description: '',
      properties: { initialValue: x.inicial ?? 50, color: null },
      _ordre: i,
    })).map(({ _ordre, ...rest }) => rest),
    relationships: arquetip.relacions.map((x, i) => ({
      id: `rel_${arquetip.id}_${ara}_${i}`,
      source: idNou(x.from),
      target: idNou(x.to),
      polarity: x.polarity,
      delay: x.delay,
      strength: x.strength ?? 'medium',
      name: '',
      description: '',
    })),
  };
}

const ARQ = { ARQUETIPS, arquetipPerId, capAlLlenc };
export default ARQ;
